import React from "react";
import {
  Mail,
  BarChart3,
  Plus,
  Settings,
  FileText,
  Users,
  Calendar,
} from "lucide-react";
import PageHeader from "../../../components/marketing/cloud-dashboard/header";
import { useNavigate } from "react-router-dom";

const EmailDashboard: React.FC = () => {
  // Mock data for email campaigns
  const navigate = useNavigate();
  const campaigns = [
    {
      id: 1,
      name: "Welcome Series",
      sent: "12.5K",
      openRate: "42%",
      clickRate: "8.2%",
      status: "active",
    },
    {
      id: 2,
      name: "Product Announcement",
      sent: "8.7K",
      openRate: "38%",
      clickRate: "6.5%",
      status: "completed",
    },
    {
      id: 3,
      name: "Weekly Newsletter",
      sent: "23.1K",
      openRate: "45%",
      clickRate: "9.1%",
      status: "scheduled",
    },
  ];

  const stats = [
    { label: "Sent", value: "892K", change: "+12.4%" },
    { label: "Opened", value: "24.8%", change: "+2.1%" },
    { label: "Clicked", value: "3.2%", change: "+0.8%" },
  ];
  function onBack() {
    navigate("/cpas-dashboard");
  }

  function onActionButtonClick() {
    navigate("/campaigns/create");
  }

  return (
    <>
      <PageHeader
        title="Email Marketing"
        subtitle="Professional email campaigns with advanced automation"
        icon={<Mail className="text-purple-600" />}
        onBack={onBack}
        actionButtonText="Create Campaign"
        onActionButtonClick={onActionButtonClick}
        actionButtonIcon={<Plus size={16} />}
      />

      <div className="max-w-5xl mx-auto ">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <Mail className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Email Marketing
          </h1>
          <p className="text-gray-600 mb-4">
            Professional email campaigns with advanced automation
          </p>
          <div className="inline-flex items-center space-x-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span>Active & Connected</span>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-8">
          <h3 className="text-2xl font-bold mb-5">Recent Performance</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ">
            {stats.map((stat, index) => (
              <div key={index}>
                <p className="text-gray-600 text-sm mb-2">{stat.label}</p>
                <div className="flex items-end justify-between">
                  <h3 className="text-2xl font-bold text-gray-900">
                    {stat.value}
                  </h3>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-1 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Recent Campaigns */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  Recent Campaigns
                </h2>
                <button className="text-blue-600 text-sm font-medium">
                  View all
                </button>
              </div>
              <div className="space-y-4">
                {campaigns.map((campaign) => (
                  <div
                    key={campaign.id}
                    className="flex items-center justify-between p-4 border border-gray-100 rounded-lg"
                  >
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {campaign.name}
                      </h3>
                      <div className="flex items-center space-x-4 mt-1">
                        <span className="text-sm text-gray-600">
                          Sent: {campaign.sent}
                        </span>
                        <span className="text-sm text-gray-600">
                          Open: {campaign.openRate}
                        </span>
                        <span className="text-sm text-gray-600">
                          Click: {campaign.clickRate}
                        </span>
                      </div>
                    </div>
                    <div
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        campaign.status === "active"
                          ? "bg-green-100 text-green-800"
                          : campaign.status === "completed"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {campaign.status}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                Quick Actions
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button className="flex items-center space-x-3 p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-left">
                  <FileText size={20} className="text-blue-600" />
                  <div>
                    <div className="font-medium text-gray-900">
                      Create Template
                    </div>
                    <div className="text-sm text-gray-600">
                      Design a new email template
                    </div>
                  </div>
                </button>
                <button className="flex items-center space-x-3 p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-left">
                  <Users size={20} className="text-green-600" />
                  <div>
                    <div className="font-medium text-gray-900">
                      Manage Lists
                    </div>
                    <div className="text-sm text-gray-600">
                      Create and segment email lists
                    </div>
                  </div>
                </button>
                <button className="flex items-center space-x-3 p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-left">
                  <BarChart3 size={20} className="text-purple-600" />
                  <div>
                    <div className="font-medium text-gray-900">
                      View Analytics
                    </div>
                    <div className="text-sm text-gray-600">
                      Check performance metrics
                    </div>
                  </div>
                </button>
                <button className="flex items-center space-x-3 p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-left">
                  <Calendar size={20} className="text-orange-600" />
                  <div>
                    <div className="font-medium text-gray-900">Schedule</div>
                    <div className="text-sm text-gray-600">
                      Plan future campaigns
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6 w-full">
            {/* Features */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Key Features
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm text-gray-700">
                    Drag & drop email builder
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm text-gray-700">
                    A/B testing capabilities
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm text-gray-700">
                    Advanced segmentation
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm text-gray-700">
                    Automated drip campaigns
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm text-gray-700">
                    Responsive design testing
                  </span>
                </li>
              </ul>
            </div>

            {/* Resources */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Resources
              </h3>
              <div className="space-y-3">
                <button className="w-full text-left p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="font-medium text-gray-900">
                    📖 Documentation
                  </div>
                  <div className="text-sm text-gray-600">
                    Complete setup guide
                  </div>
                </button>
                <button className="w-full text-left p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="font-medium text-gray-900">
                    🔧 API Reference
                  </div>
                  <div className="text-sm text-gray-600">
                    Developer integration docs
                  </div>
                </button>
                <button className="w-full text-left p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="font-medium text-gray-900">
                    💡 Best Practices
                  </div>
                  <div className="text-sm text-gray-600">
                    Tips for better engagement
                  </div>
                </button>
              </div>
            </div>

            {/* Support */}
            <div className="bg-blue-50 rounded-xl border border-blue-200 p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">
                Need Support?
              </h3>
              <p className="text-sm text-blue-700 mb-4">
                Our team is here to help you get started
              </p>
              <button className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors">
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmailDashboard;
